import type { VariantProps } from 'class-variance-authority';

import type { ItineraryItemType } from '@elara/domain/models/schemas/itinerary-item-schemas';
import type { MessageRef } from '@elara/domain/models/schemas/shortlist-schemas';

import type { buttonVariants } from './button-variants.js';

import React, { useMemo, useState } from 'react';

import { cn, metadataForIdentity } from '@elara/client-core/lib/utils';

import { useItineraryState, useTripsState } from '#states/hooks.js';

import { Button } from './button.js';
import { useCompactGuideActions } from './guides/guide-header-actions.js';
import { useItemAvailability } from './hooks/useItemAvailability.js';
import { ItineraryDatesDialog } from './itinerary-dates-dialog.js';
import { MaterialIcon } from './MaterialIcon.js';

export interface ItineraryButtonProps {
  /** The type of item being added to itinerary */
  itemType: ItineraryItemType;
  /** The reference ID of the item (e.g., hotelId, flightId, roomOptionId) */
  referenceId?: string;
  /** The title to display for the itinerary item */
  title: string;
  /** Optional reference to the message where this recommendation was made */
  messageRef?: MessageRef;
  /** Optional metadata for identity (e.g. room: { productCode, occupancy }) */
  metadata?: Record<string, unknown>;
  /** Optional explicit start datetime (ISO string) for the itinerary item */
  startDateTime?: string;
  /** Optional explicit end datetime (ISO string) for the itinerary item */
  endDateTime?: string;
  /**
   * For room items: how many copies to create with this rate + occupancy. Each is a
   * separate itinerary item. Passed to the AddItineraryItem flow at the input level
   * (NOT stored on the resulting items).
   */
  quantity?: number;
  /**
   * Whether the user must confirm / edit dates before adding to the itinerary.
   * If false and startDateTime is provided, the item will be added directly without showing the dialog.
   */
  requireDateConfirmation?: boolean;
  /** Additional class name */
  className?: string;
  /** Variant of the button */
  variant?: 'default' | 'icon';
  /** Size for icon variant */
  size?: 'icon' | 'icon-sm' | 'icon-xs';
  buttonText?: string;
  /**
   * Underlying Button visual variant (only applied when `variant === 'default'`).
   * Defaults to `'default'` (primary). Use `'outline'` or `'secondary'` to
   * de-emphasise the button when it sits next to a more important CTA.
   */
  buttonVariant?: VariantProps<typeof buttonVariants>['variant'];
  /**
   * Underlying Button size (only applied when `variant === 'default'`). Mirrors `buttonVariant`; the
   * `size` prop above governs the ICON variant only. Defaults to the Button's own default.
   */
  buttonSize?: VariantProps<typeof buttonVariants>['size'];
}

export function ItineraryButton({
  itemType,
  referenceId,
  title,
  messageRef,
  metadata,
  startDateTime,
  endDateTime,
  quantity,
  requireDateConfirmation,
  className,
  variant: variantProp,
  size: sizeProp,
  buttonText = 'Add to trip',
  buttonVariant = 'default',
  buttonSize,
}: ItineraryButtonProps) {
  // In a guide panel's floating header (`guide-header-actions`) the button takes its icon-only form.
  const compact = useCompactGuideActions();
  const variant = variantProp ?? (compact ? 'icon' : 'default');
  const size = sizeProp ?? (compact ? 'icon-sm' : 'icon');
  const itineraryState = useItineraryState();
  const tripsState = useTripsState();

  const [isHovered, setIsHovered] = useState(false);
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const hasTrip = !!tripsState.current;
  const identityMeta = useMemo(() => metadataForIdentity(itemType, metadata), [itemType, metadata]);
  // `quantity` is shortlist-intent only — it must not be stored on the resulting itinerary
  // items. The identity metadata (productCode + occupancy for rooms) is what gets persisted.
  const itemMetadata = identityMeta ?? metadata;
  const isInItinerary = referenceId != null ? itineraryState.isInItinerary(itemType, referenceId, identityMeta) : false;

  const itineraryItem = useMemo(() => {
    return referenceId != null ? itineraryState.getItineraryItemByReference(itemType, referenceId, identityMeta) : null;
  }, [referenceId, itineraryState, itemType, identityMeta]);

  const isBooked = itineraryItem?.isBooked;
  // Rooms become Elara-fulfilled once booked or approved, so the toggle can no
  // longer remove them — the booking has to be cancelled first. A pending
  // cancellation stays locked too (the reservation may still be live). Other
  // types only lock once booked (unchanged behaviour).
  const removalLocked =
    isBooked ||
    (itemType === 'room' && (itineraryItem?.isApproved === true || itineraryItem?.isCancellationPending === true));
  const isUnavailable = useItemAvailability(itemType, referenceId, metadata);
  const isDisabled = isLoading || !hasTrip || removalLocked || (isUnavailable && !isInItinerary);

  // Determine icon based on state
  const icon = isInItinerary
    ? isHovered
      ? 'event_busy'
      : 'event_available'
    : isHovered
      ? 'event_upcoming'
      : 'calendar_today';

  // For flight/room, no date dialog (server resolves dates). For others, show dialog when dates missing.
  const canAddWithoutDialog = ['flight', 'room'].includes(itemType);
  const shouldConfirmDates = requireDateConfirmation ?? (canAddWithoutDialog ? false : !startDateTime);

  const handleClick = async () => {
    if (!hasTrip) return;

    // If already in itinerary, remove ALL matching items (room multi-bookings produce
    // multiple items with the same rate + occupancy). For single-item types this is
    // identical to removing the one match. Confirmation is shown by the dialog only
    // for room single-item removal.
    if (isInItinerary && referenceId != null) {
      const tripId = tripsState.current?.id;
      if (!tripId) return;
      const matches = itineraryState.getAllItineraryItemsByReference(itemType, referenceId, identityMeta);
      if (matches.length === 1) {
        itineraryState.openDeleteDialog(matches[0]!);
        return;
      }
      if (matches.length > 1) {
        setIsLoading(true);
        try {
          await Promise.all(matches.map(async (m) => itineraryState.deleteItem(m.id, tripId)));
        } finally {
          setIsLoading(false);
        }
        return;
      }
    }

    // Add directly when no dialog required (flight/room without dates, or any type with explicit dates)
    if (!shouldConfirmDates && (startDateTime || canAddWithoutDialog)) {
      setIsLoading(true);
      try {
        await itineraryState.addItemToItinerary({
          type: itemType,
          title,
          referenceId,
          metadata: itemMetadata,
          ...(quantity != null && quantity > 1 && { quantity }),
          ...(startDateTime != null && startDateTime !== '' && { startDateTime }),
          ...(endDateTime != null && endDateTime !== '' && { endDateTime }),
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // For other items, open date dialog
    setIsDateDialogOpen(true);
  };

  const handleConfirmDates = async (dates: { startDateTime: string; endDateTime?: string }) => {
    setIsLoading(true);
    try {
      await itineraryState.addItemToItinerary({
        type: itemType,
        title,
        referenceId,
        metadata,
        ...(quantity != null && quantity > 1 && { quantity }),
        startDateTime: dates.startDateTime,
        endDateTime: dates.endDateTime,
      });
      setIsDateDialogOpen(false); // Close on success
    } catch (error) {
      // Error toast is already shown by ItineraryState
      // Keep dialog open so user can try again or cancel
      console.error('Failed to add to itinerary', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Prefill dates from props or trip dates
  const initialStartDateTime = useMemo(() => {
    if (startDateTime) return startDateTime;
    return tripsState.current?.startDate ?? undefined;
  }, [startDateTime, tripsState]);

  const initialEndDateTime = useMemo(() => {
    if (startDateTime) return startDateTime;
    return tripsState.current?.startDate ?? undefined;
  }, [startDateTime, tripsState]);

  const buttonLabel = isInItinerary ? (isBooked ? 'Booked' : 'Added to trip') : (buttonText ?? 'Add to trip');

  const tooltip = isInItinerary ? 'Remove from itinerary' : 'Add to itinerary';

  if (variant === 'icon') {
    return (
      <>
        <Button
          variant="icon"
          size={size}
          disabled={isDisabled}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          tooltip={isDisabled ? undefined : tooltip}
          className={cn(
            'hover:bg-accent/10 hover:text-accent',
            isInItinerary && 'text-accent hover:text-accent hover:bg-accent/10',
            className,
          )}
        >
          <MaterialIcon icon={icon} filled={isInItinerary} />
        </Button>
        {isDateDialogOpen && (
          <ItineraryDatesDialog
            open={isDateDialogOpen}
            onOpenChange={setIsDateDialogOpen}
            itemType={itemType}
            referenceId={referenceId}
            title={title}
            initialStartDateTime={initialStartDateTime}
            initialEndDateTime={initialEndDateTime}
            tripStartDate={tripsState.current?.startDate ?? undefined}
            tripEndDate={tripsState.current?.endDate ?? undefined}
            onConfirm={handleConfirmDates}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        disabled={isDisabled}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn('group', isInItinerary && 'bg-accent border-accent text-accent-foreground', className)}
      >
        <MaterialIcon icon={icon} filled={isInItinerary} />
        {buttonLabel}
      </Button>
      {isDateDialogOpen && (
        <ItineraryDatesDialog
          open={isDateDialogOpen}
          onOpenChange={setIsDateDialogOpen}
          itemType={itemType}
          referenceId={referenceId}
          title={title}
          initialStartDateTime={initialStartDateTime}
          initialEndDateTime={initialEndDateTime}
          tripStartDate={tripsState.current?.startDate ?? undefined}
          tripEndDate={tripsState.current?.endDate ?? undefined}
          onConfirm={handleConfirmDates}
        />
      )}
    </>
  );
}
